import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, formatDistanceStrict } from 'date-fns';
import { useParams } from 'react-router-dom';
import type { PlayEventMatch, PublicPlayEventParticipant } from '@court-sync/shared';
import {
  formatPublicBoardMatchStatusLabel,
  formatPublicBoardRoundHeadingLabel,
  formatPublicBoardRoundTimingLabel,
  getPlayEventParticipantDisplayName,
  isPlayEventBoardFinished,
  isPlayEventBoardEndingSoon,
  isPlayEventBoardLive,
  resolvePublicBoardRoundNumber,
  resolveRoundStartTime,
  resolveSingleEliminationPodium,
  sortPlayEventMatchesForDisplay,
} from '@court-sync/shared';
import { CalendarClock, CheckCircle2, Play, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PlayEventDisplayBrandMark } from '@/components/play/PlayEventDisplayBrandMark';
import { PlayEventDisplayClock } from '@/components/play/PlayEventDisplayClock';
import { PlayEventDisplayShell } from '@/components/play/PlayEventDisplayShell';
import { PlayEventDisplayBracketTree } from '@/components/play/PlayEventDisplayBracketTree';
import { PlayEventMatchupCard } from '@/components/play/PlayEventMatchupCard';
import { PlayEventParticipantRow } from '@/components/play/PlayEventParticipantAvatar';
import { playEventDisplayTheme } from '@/components/play/playEventDisplayTheme';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { playEventService, type PlayEventPublicBoardResponse } from '@/services/playEvent';
import { PLAY_EVENT_STATUS_LABELS } from '@/lib/playEventLabels';

/** Live round matchups — refresh often enough for court/status changes. */
const PUBLIC_BOARD_POLL_MS = 30_000;
/** Bracket-only kiosk view — slow pan can exceed 30s on large draws. */
const PUBLIC_BOARD_BRACKET_ONLY_POLL_MS = 120_000;

function parseEventDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function formatEventWindow(startsAt: Date | string, endsAt: Date | string): string {
  const start = parseEventDate(startsAt);
  const end = parseEventDate(endsAt);
  return `${format(start, 'd MMM yyyy')} · ${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
}

function sortMatchesForBoard(
  matches: PlayEventMatch[],
  courtNamesById: Record<string, string>
): PlayEventMatch[] {
  return [...matches].sort((left, right) => {
    const leftCourt = left.courtId ? courtNamesById[left.courtId] ?? '' : '';
    const rightCourt = right.courtId ? courtNamesById[right.courtId] ?? '' : '';
    return leftCourt.localeCompare(rightCourt, undefined, { numeric: true, sensitivity: 'base' });
  });
}

function getBoardEmptyMessage(
  board: PlayEventPublicBoardResponse | null,
  now: Date
): string {
  if (!board?.event) return 'Loading event…';

  const { event, drawStarted, nextReveal } = board;

  if (!drawStarted) {
    if (event.status === 'completed') return 'This event has finished.';
    if (event.status === 'cancelled') return 'This event was cancelled.';
    if (board.bracket?.isPreview) {
      return 'Court assignments and live round detail appear when the operator starts the event.';
    }
    return 'Matchups will appear here after the operator starts the event.';
  }

  if (board.matches.length > 0) {
    return 'No upcoming matches right now.';
  }

  if (nextReveal) {
    const revealAt = parseEventDate(nextReveal.revealAt);
    if (revealAt.getTime() > now.getTime()) {
      return `Round ${nextReveal.roundNumber} matchups in ${formatDistanceStrict(revealAt, now)}`;
    }
  }

  if (event.status === 'completed') return 'This event has finished.';
  return 'No matchups to display right now.';
}

export default function PlayEventPublicBoardPage() {
  const orgSlug = useOrgSlug();
  const { profile } = useOrgPublicProfile();
  const organization = profile?.organization;
  const { slug: eventSlug } = useParams<{ slug?: string }>();
  const [board, setBoard] = useState<PlayEventPublicBoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const [celebrateMatchIds, setCelebrateMatchIds] = useState<Set<string>>(() => new Set());
  const [celebrateSlotKeys, setCelebrateSlotKeys] = useState<Set<string>>(() => new Set());
  const matchSnapshotRef = useRef<Record<string, string>>({});

  const loadBoard = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!orgSlug || !eventSlug) return;
    if (!silent) {
      setLoading(true);
    }
    try {
      const res = await playEventService.getPublicEventBoard(orgSlug, eventSlug);
      if (res.success && res.data) {
        setBoard((previous) => {
          if (previous && JSON.stringify(previous) === JSON.stringify(res.data)) {
            return previous;
          }
          return res.data;
        });
      } else if (!silent) {
        setBoard(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [orgSlug, eventSlug]);

  const bracketOnlyModeRef = useRef(false);

  useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    let timeoutId = 0;
    let cancelled = false;

    const scheduleNextPoll = () => {
      if (cancelled) return;
      const pollMs = bracketOnlyModeRef.current
        ? PUBLIC_BOARD_BRACKET_ONLY_POLL_MS
        : PUBLIC_BOARD_POLL_MS;
      timeoutId = window.setTimeout(() => {
        void loadBoard({ silent: true }).finally(() => {
          if (!cancelled) scheduleNextPoll();
        });
      }, pollMs);
    };

    scheduleNextPoll();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [loadBoard]);

  useEffect(() => {
    const clockInterval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clockInterval);
  }, []);

  const participantsById = useMemo(() => {
    const map = new Map<string, PublicPlayEventParticipant>();
    for (const participant of board?.participants ?? []) {
      map.set(participant.id, participant);
    }
    return map;
  }, [board?.participants]);

  const teamsById = useMemo(() => {
    const map = new Map(board?.teams.map((team) => [team.id, team]) ?? []);
    return map;
  }, [board?.teams]);

  const event = board?.event;
  const courtNamesById = board?.courtNamesById ?? {};
  const eventFinished = event ? isPlayEventBoardFinished(event, now) : false;
  const eventLive = event ? isPlayEventBoardLive(event, now) : false;
  const eventEndingSoon = event ? isPlayEventBoardEndingSoon(event, now) : false;
  const eventCancelled = event?.status === 'cancelled';
  const emptyMessage = getBoardEmptyMessage(board, now);

  const currentRoundNumber = useMemo(
    () =>
      resolvePublicBoardRoundNumber(board?.matches ?? [], {
        now,
        event: event ?? null,
        rounds: board?.rounds ?? null,
      }),
    [board?.matches, board?.rounds, event, now]
  );

  const currentRoundMatches = useMemo(() => {
    if (currentRoundNumber == null) return [];
    const roundMatches = (board?.matches ?? []).filter(
      (match) => match.roundNumber === currentRoundNumber
    );
    return sortMatchesForBoard(roundMatches, courtNamesById);
  }, [board?.matches, courtNamesById, currentRoundNumber]);

  const currentRound = useMemo(
    () => board?.rounds.find((round) => round.roundNumber === currentRoundNumber) ?? null,
    [board?.rounds, currentRoundNumber]
  );

  const currentRoundStart = useMemo(() => {
    if (!event || currentRoundNumber == null) return null;
    return resolveRoundStartTime(currentRound, currentRoundNumber, event);
  }, [currentRound, currentRoundNumber, event]);

  const sittingOut = currentRoundNumber
    ? board?.sittingOutByRound?.[currentRoundNumber] ?? []
    : [];

  const bracket = board?.bracket ?? null;
  const showBracket = event?.format === 'single_elimination' && (bracket?.matches.length ?? 0) > 0;
  const bracketOnlyMode = showBracket && !loading && currentRoundMatches.length === 0;

  bracketOnlyModeRef.current = bracketOnlyMode;

  const podium = useMemo(() => {
    if (!board?.matches.length || !event?.roundCount) {
      return null;
    }
    const { winnerParticipantId, runnerUpParticipantId } = resolveSingleEliminationPodium(
      board.matches,
      event.roundCount
    );
    if (!winnerParticipantId) {
      return null;
    }

    const winner = board.participants.find((participant) => participant.id === winnerParticipantId);
    const runnerUp = runnerUpParticipantId
      ? board.participants.find((participant) => participant.id === runnerUpParticipantId)
      : null;

    return {
      winnerName: winner ? getPlayEventParticipantDisplayName(winner) : 'Winner',
      runnerUpName: runnerUp ? getPlayEventParticipantDisplayName(runnerUp) : null,
    };
  }, [board?.matches, board?.participants, event?.roundCount]);

  useEffect(() => {
    if (!board?.matches?.length) {
      return undefined;
    }

    const nextSnapshot: Record<string, string> = {};
    const nextCelebrateMatchIds = new Set<string>();
    const nextCelebrateSlotKeys = new Set<string>();
    const matchIndexById = new Map<string, number>();
    let globalMatchIndex = 0;
    const roundNumbers = [...new Set(board.matches.map((match) => match.roundNumber))].sort(
      (left, right) => left - right
    );

    for (const roundNumber of roundNumbers) {
      const roundMatches = sortPlayEventMatchesForDisplay(
        board.matches.filter((match) => match.roundNumber === roundNumber)
      );
      for (const match of roundMatches) {
        matchIndexById.set(match.id, globalMatchIndex);
        globalMatchIndex += 1;
      }
    }

    for (const match of board.matches) {
      const snapshot = `${match.result}:${match.winnerSide ?? ''}`;
      const previousSnapshot = matchSnapshotRef.current[match.id];
      if (
        previousSnapshot &&
        previousSnapshot !== snapshot &&
        match.result !== 'unplayed' &&
        match.winnerSide
      ) {
        nextCelebrateMatchIds.add(match.id);
        const matchIndex = matchIndexById.get(match.id);
        if (matchIndex != null) {
          nextCelebrateSlotKeys.add(`${match.roundNumber}-${matchIndex}-${match.winnerSide}`);
        }
      }
      nextSnapshot[match.id] = snapshot;
    }

    matchSnapshotRef.current = nextSnapshot;

    if (nextCelebrateMatchIds.size === 0) {
      return undefined;
    }

    setCelebrateMatchIds(nextCelebrateMatchIds);
    setCelebrateSlotKeys(nextCelebrateSlotKeys);
    const timeoutId = window.setTimeout(() => {
      setCelebrateMatchIds(new Set());
      setCelebrateSlotKeys(new Set());
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [board?.matches]);

  return (
    <PlayEventDisplayShell
      primaryColor={organization?.primaryColor}
      secondaryColor={organization?.secondaryColor}
    >
      <header className={playEventDisplayTheme.header}>
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
            <PlayEventDisplayBrandMark
              logoUrl={organization?.logoUrl}
              organizationName={organization?.name}
            />
            <div className="min-w-0">
              <p className={`mb-1 ${playEventDisplayTheme.accentLabel}`}>Matchup board</p>
              <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">
                {event?.name ?? 'Play event'}
              </h1>
              {event ? (
                <p className={`mt-1 flex flex-wrap items-center gap-2 ${playEventDisplayTheme.muted}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarClock className="h-4 w-4 shrink-0" aria-hidden />
                    {formatEventWindow(event.startsAt, event.endsAt)}
                  </span>
                  <span className={playEventDisplayTheme.badge}>
                    {PLAY_EVENT_STATUS_LABELS[event.status]}
                  </span>
                </p>
              ) : null}
            </div>
          </div>

          <PlayEventDisplayClock
            now={now}
            eventLive={eventLive}
            eventFinished={eventFinished}
            eventEndingSoon={eventEndingSoon}
          />
        </div>
      </header>

      <main className={playEventDisplayTheme.main}>
        {eventFinished ? (
          <section
            className={playEventDisplayTheme.finishedSection}
            aria-live="polite"
          >
            <div className={playEventDisplayTheme.finishedIconWrap}>
              <CheckCircle2 className={playEventDisplayTheme.finishedIcon} aria-hidden />
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {eventCancelled ? 'Event was cancelled' : 'Event is finished'}
            </h2>
            {event ? (
              <p className={`mt-3 max-w-md text-base ${playEventDisplayTheme.muted}`}>
                {eventCancelled
                  ? `${event.name} was cancelled.`
                  : `Thanks for playing. ${event.name} has ended.`}
              </p>
            ) : null}
          </section>
        ) : (
          <section
            className={cn(
              playEventDisplayTheme.section,
              bracketOnlyMode && 'overflow-hidden'
            )}
          >
            <div className="shrink-0">
              {podium ? (
                <div
                  className={cn(
                    playEventDisplayTheme.panel,
                    'mb-4 flex flex-wrap items-center gap-3 border-emerald-500/30 bg-emerald-50/80 p-4 dark:bg-emerald-500/10'
                  )}
                  aria-live="polite"
                >
                  <Trophy className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  <div>
                    <p className="text-sm font-semibold text-emerald-950 dark:text-emerald-100">
                      Champion: {podium.winnerName}
                    </p>
                    {podium.runnerUpName ? (
                      <p className={`text-sm ${playEventDisplayTheme.mutedSubtle}`}>
                        Runner-up: {podium.runnerUpName}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className={`mb-2 ${playEventDisplayTheme.sectionHeading}`}>
                    <Play className="mr-2 inline h-4 w-4" aria-hidden />
                    {bracketOnlyMode ? 'Bracket' : 'Matchups'}
                  </h2>
                  {event && !bracketOnlyMode ? (
                    <p className={`mb-4 ${playEventDisplayTheme.mutedSubtle}`}>
                      Each round appears {event.matchupVisibilityMinutes} minutes before it starts.
                    </p>
                  ) : (
                    <div className="mb-2" />
                  )}
                </div>
                {bracketOnlyMode ? (
                  <p
                    className={`max-w-md text-right text-sm font-medium text-gray-700 dark:text-white/70`}
                    aria-live="polite"
                  >
                    {emptyMessage}
                  </p>
                ) : null}
              </div>
            </div>

            <div
              className={cn(
                'min-h-0 flex-1',
                bracketOnlyMode ? 'flex flex-col overflow-hidden' : 'overflow-y-auto overscroll-contain'
              )}
            >
              {loading ? (
                <div className="flex min-h-full items-center justify-center py-8" aria-busy="true">
                  <div className={playEventDisplayTheme.spinner} />
                </div>
              ) : bracketOnlyMode && bracket ? (
                <PlayEventDisplayBracketTree
                  matches={bracket.matches}
                  participantNamesById={bracket.participantNamesById}
                  isPreview={bracket.isPreview}
                  autoScrollLoop
                  compactFooter
                  celebrateSlotKeys={celebrateSlotKeys}
                  className="min-h-0 flex-1"
                />
              ) : (
                <div className="space-y-6 pb-1">
                  {showBracket && bracket ? (
                    <div className={playEventDisplayTheme.panel + ' p-4 sm:p-5'}>
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Bracket
                        </h3>
                        <p className={`mt-1 text-xs ${playEventDisplayTheme.mutedSubtle}`}>
                          {bracket.isPreview
                            ? 'Full draw path from current entries.'
                            : 'Full elimination path — later rounds fill in as matches are played.'}
                        </p>
                      </div>
                      <PlayEventDisplayBracketTree
                        matches={bracket.matches}
                        participantNamesById={bracket.participantNamesById}
                        isPreview={bracket.isPreview}
                        celebrateSlotKeys={celebrateSlotKeys}
                      />
                    </div>
                  ) : null}

                  {currentRoundMatches.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
                      <p className={playEventDisplayTheme.bodyText}>{emptyMessage}</p>
                    </div>
                  ) : (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-widest ${playEventDisplayTheme.mutedFaint}`}>
                        {currentRoundStart
                          ? formatPublicBoardRoundHeadingLabel(
                              currentRoundStart,
                              now,
                              event?.roundDurationMinutes
                            )
                          : 'Current round'}
                      </p>
                      <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">
                        Round {currentRoundNumber}
                      </h3>
                    </div>
                    {currentRoundStart ? (
                      <p className={playEventDisplayTheme.mutedFaint}>
                        {formatPublicBoardRoundTimingLabel(
                          currentRoundStart,
                          now,
                          event?.roundDurationMinutes
                        )}
                      </p>
                    ) : null}
                  </div>

                  <ul className="grid gap-4 lg:grid-cols-2">
                    {currentRoundMatches.map((match) => (
                      <li key={match.id}>
                        <PlayEventMatchupCard
                          match={match}
                          participantsById={participantsById}
                          teamsById={teamsById}
                          courtName={match.courtId ? courtNamesById[match.courtId] : null}
                          celebrateWinner={celebrateMatchIds.has(match.id)}
                          statusLabel={
                            currentRoundStart
                              ? formatPublicBoardMatchStatusLabel(
                                  match,
                                  now,
                                  currentRoundStart,
                                  event?.roundDurationMinutes
                                )
                              : match.result.replace(/_/g, ' ')
                          }
                        />
                      </li>
                    ))}
                  </ul>

                  {sittingOut.length > 0 ? (
                    <div className={playEventDisplayTheme.sittingOut}>
                      <p className={`mb-3 ${playEventDisplayTheme.sittingOutLabel}`}>Sitting out</p>
                      <ul className="flex flex-wrap gap-3">
                        {sittingOut.map((participant) => (
                          <li key={participant.id}>
                            <PlayEventParticipantRow participant={participant} size="sm" />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </PlayEventDisplayShell>
  );
}
